

  
const content = `# Database Encryption Best Practices and Limitations
OnData provides innovative approaches to secure and protect sensitive data in your databases while preserving the data formats. The algorithms OnData uses for encryption 
are based on AES-256 encryption mechanism and are approved by the National Institute of Standards and Technology (NIST). 

Based on data classification, the sensitive data, confidential data and regulated data can be automatically encrypted for enhanced security. Such data will stay encrypted at rest, 
in transit and even at runtime. It will only be decrypted for a user when such user has been explicitly granted necessary permissions to access the data. An organization can 
not only configure but also enforce the data access policies on all sensitive data across the organization.

## Best Practices
The OnData Encryption feature is intended to protect sensitive, confidential and regulated data. To achieve that purpose without comprising the structure or integrity 
of the databases, please follow the OnData Encryption best practices listed below.

- It is recommended to encrypt only fields that are sensitive in nature and should be protected, such as Personal Identifiable Information (PII). You may perform 
data classification on data fields first. If a data field is not classified as sensitive, confidential or regulated, there is no need to apply any encryption 
rule to that data field. The encryption and decryption process does introduce some overhead to the data exchange with databases. Such overhead 
should be avoided if there is no real business need to protect a data field that is not sensitive in nature;

- Do not encrypt any identity columns. The values of an identity column are automatically generated by the database engine. Such columns are typically used as 
primary keys and foreign keys in tables. There is no need to protect such data fields, and encryption may cause issues to the relationships among the entities.

## General Limitations
The OnData encryption feature has certain limitations. Please review the details below before encrypting your data.

### Encryption Rule Exceptions
An encryption rule can be applied to any column to encrypt the data in that column. There are a number of exceptions that a column should not be assigned any 
encryption rule.
- If a column does not contain any sensitive data, do not assign any encryption rule
- If a column is an identity column with automatically generated values, do not assign any encryption rule
- If a column is a computed column, do not assign any encryption rule. The values of such columns are computed by the database engine
- If your database queries use non-equality comparison operators on a column, do not assign any encryption rule to that column. For example, if your queries 
use these operators: ">", ">=", "<", "<=", "LIKE" and "NOT LIKE" on a table column, such queries may not work with if that column is encrypted. You can either do 
not apply any encryption rules to such column, or revise the queries to remove the operators listed above. The equality operators such as "= (equality)" 
and "<> (inequality)" are allowed for an encrypted column
- If you need to correlate a column in one database with a column in another database, do not assign any encryption rule to those columns unless you have configured 
the exact same encryption key for both databases

### Stored Procedures
If you use stored procedures in your databases, such stored procedures may not work as desired when some of the columns referenced in the stored procedures are 
encrypted. You may not assign an encryption rule to a column whose values are manipulated in stored procedures. 

### Triggers
If you use triggers to automatically perform certain actions based on the values of a column exceeding certain threshold, that column should not be assigned an 
encryption rule.

### Constraints
To allow randomness of the encrypted results, some of the database constraints may have to be removed. If you have constraints configured for a column to limit 
the possible range of values, you may remove such constraints before applying encryption, or alternatively do not assign an encryption rule to that column. For 
example, it makes sense to have a database constraint to limit the birthdates to dates in the past. But the encryption engine may generate a date in the future. 
Such constraint may need to be removed before encryption can be applied. Alternatively, you may use the "Year Preserved Date" encryption rule to encrypt the month 
and day only.

## Database Specific Limitations

### Limitations for SQL Server

- Currently the system-versioned temporal tables of SQL Server cannot be processed.
- Data subsetting is not available for graph tables.

### Limitations for Oracle

- The object table, external table, queue table, read-only table and materialized view cannot be processed.
- Data subsetting is not available for read only tables, so alter the tables to 'read write' manually if you need to do data subsetting for them. Otherwise, 
the read only tables will be ignored.
- For referenced partition tables, the partition key columns cannot be encrypted even if the table row movement is enabled. For other partitioned tables, the 
partition key columns cannot be encrypted if the table row movement is not enabled.

### Limitations for MongoDB

Please refer to the [MongoDB Encryption Limitations](reference_doc#best_practices_and_limitations/database_product/mongodb_encryption_limitations) for more details.
`
  
export default content;
